import React from 'react';
import { Table, Tag, Button, Space, Popconfirm, message, Input, Grid, Card } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { deleteOrder } from '../../store/ordersSlice';
import { fetchShops } from '../../store/shopsSlice';
import { formatCurrency } from '../../utils/formatters';
import { generateInvoicePDF } from '../../utils/invoiceGenerator';
import { exportToExcel } from '../../utils/exportUtils';

const OrderList = () => {
  const { orders } = useSelector((state) => state.orders);
  const { user } = useSelector((state) => state.auth);
  const { shops } = useSelector((state) => state.shops);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [invoiceLoading, setInvoiceLoading] = React.useState(null);

  React.useEffect(() => {
    dispatch(fetchShops());
  }, [dispatch]);

  const handleInvoice = async (order, shop, bde, action) => {
    try {
      setInvoiceLoading(order.id);
      await generateInvoicePDF(order, shop, bde, action);
      if (action === 'download') {
        message.success('Invoice downloaded successfully');
      }
    } catch (error) {
      message.error('Failed to generate invoice: ' + error.message);
    } finally {
      setInvoiceLoading(null);
    }
  };

  const myOrders = orders.filter(o => o.bdeId === user.id);
  const filtered = myOrders.filter((o) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    const id = String(o.id || '').toLowerCase();
    const name = String(o.customerName || '').toLowerCase();
    const status = String(o.status || '').toLowerCase();
    const date = String(o.createdAt || '').toLowerCase();
    return id.includes(q) || name.includes(q) || status.includes(q) || date.includes(q);
  });

  const handleExport = () => {
    if (!filtered || filtered.length === 0) {
      message.warning('No orders to export');
      return;
    }

    const dataToExport = filtered.map(o => {
      const shop = shops.find(s => s.id === o.shopId);
      return {
        'Order ID': o.id,
        'Shop Name': o.customerName,
        'Shop Owner': shop ? shop.ownerName : 'N/A',
        'Shop Mobile': shop ? shop.contactNumber : 'N/A',
        'Amount (₹)': o.amount,
        Status: o.status,
        'Created At': new Date(o.createdAt).toLocaleString(),
        'Commission Paid': o.commissionPaid ? 'Yes' : 'No'
      };
    });

    exportToExcel(dataToExport, 'My_Orders');
    message.success('Orders exported successfully');
  };

  const screens = Grid.useBreakpoint();

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => String(a.id).localeCompare(String(b.id)),
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      sorter: (a, b) => String(a.customerName || '').localeCompare(String(b.customerName || '')),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
      render: (status) => {
        let color = 'default';
        if (status === 'Completed') color = 'green';
        if (status === 'Pending') color = 'orange';
        if (status === 'Processing') color = 'blue';
        if (status === 'Cancelled') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => {
        const showInvoice = ['Expected', 'Processing', 'Completed'].includes(record.status);
        const shop = shops.find(s => s.id === record.shopId);
        // For BDE, 'user' is the BDE
        
        return (
          <Space size="middle">
            {showInvoice && (
              <Button 
                type="link" 
                icon={<DownloadOutlined />} 
                loading={invoiceLoading === record.id}
                onClick={() => handleInvoice(record, shop, user, 'download')}
                title="Download Invoice"
              />
            )}
            <Button type="link" onClick={() => navigate(`/bde/orders/details/${record.id}`)}>
              View
            </Button>
            <Button
              type="link"
              icon={<EditOutlined />}
              disabled={record.status === 'Expected' || record.status === 'Completed'}
              onClick={() => navigate(`/bde/orders/edit/${record.id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete the order"
              description="Are you sure to delete this order?"
              onConfirm={async () => {
                try {
                  await dispatch(deleteOrder(record.id)).unwrap();
                  message.success('Order deleted');
                } catch (err) {
                  message.error('Failed to delete order: ' + err);
                }
              }}
              okText="Yes"
              cancelText="No"
              disabled={record.status === 'Completed' || record.status === 'Expected'}
            >
              <Button type="link" danger disabled={record.status === 'Completed' || record.status === 'Expected'}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
        <h2>My Orders</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            placeholder="Search my orders"
            style={{ width: 320, maxWidth: '100%' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            Export to Excel
          </Button>
          <Button type="primary" onClick={() => navigate('/bde/orders/create')}>
            Create New Order
          </Button>
        </div>
      </div>
      {!screens.md ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(order => {
            let color = 'default';
            if (order.status === 'Completed') color = 'green';
            if (order.status === 'Pending') color = 'orange';
            if (order.status === 'Processing') color = 'blue';
            if (order.status === 'Cancelled') color = 'red';

            const canEdit = order.status !== 'Expected' && order.status !== 'Completed';
            const shop = shops.find(s => s.id === order.shopId);
            const showInvoice = ['Expected', 'Processing', 'Completed'].includes(order.status);

            return (
                <Card key={order.id} size="small" title={`#${order.id}`} extra={<Tag color={color}>{order.status}</Tag>}
                  actions={[
                    ...(showInvoice ? [<Button type="link" icon={<DownloadOutlined />} loading={invoiceLoading === order.id} onClick={() => handleInvoice(order, shop, user, 'download')}>Inv</Button>] : []),
                    <Button type="link" onClick={() => navigate(`/bde/orders/details/${order.id}`)}>View</Button>,
                    <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/bde/orders/edit/${order.id}`)} disabled={!canEdit}>Edit</Button>,
                    <Popconfirm
                    title="Delete the order"
                    description="Are you sure?"
                    onConfirm={async () => {
                      try {
                        await dispatch(deleteOrder(order.id)).unwrap();
                        message.success('Order deleted');
                      } catch (err) {
                        message.error('Failed to delete order: ' + err);
                      }
                    }}
                    okText="Yes"
                    cancelText="No"
                    disabled={order.status === 'Completed' || order.status === 'Expected'}
                  >
                    <Button type="link" danger disabled={order.status === 'Completed' || order.status === 'Expected'}>Delete</Button>
                  </Popconfirm>
                ]}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Customer:</span>
                    <span style={{ fontWeight: 500 }}>{order.customerName}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Amount:</span>
                    <span style={{ fontWeight: 600 }}>₹{order.amount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Date:</span>
                    <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Table columns={columns} dataSource={filtered} rowKey="id" scroll={{ x: 'max-content' }} />
      )}
    </div>
  );
};

export default OrderList;
